import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';
import { z } from 'zod';

/**
 * Model Router prepaid credits — typed client for the router billing endpoints.
 *
 * The backend bills router turns at provider cost plus a fee that is a SHARE OF THE
 * TOTAL CHARGE (backend ROUTER_FEE_PCT, currently 5%) — so the fee nets out of the
 * balance: a $100 top-up buys $95 of provider cost. Top-ups are credited 1:1; the
 * wallet is debited per turn. See backend docs/router_savings_pricing_design.md.
 *
 * NOTE: these endpoints return their dataclass via Django's `to_json()`, which yields
 * a JSON *string*. DRF then JSON-encodes that string, so `res.json()` gives a string
 * that must be parsed again. `decode()` handles both shapes so we never double-bug it.
 */

function decode(raw: unknown): unknown {
  return typeof raw === 'string' ? (JSON.parse(raw) as unknown) : raw;
}

async function postJson<S extends z.ZodTypeAny>(
  endpoint: string,
  body: Record<string, unknown>,
  schema: S
): Promise<z.infer<S>> {
  const res = await postWithAuth(apiUrl + endpoint, body);
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`${endpoint} failed: ${res.status} ${errText.slice(0, 200)}`);
  }
  return schema.parse(decode(await res.json())) as z.infer<S>;
}

// ── getCreditUsage ──────────────────────────────────────────────────────────
export const creditUsageSchema = z.object({
  BalanceUsd: z.number(),
  SpentUsd: z.number(),
  MarkupUsd: z.number(),
  ByModel: z.record(z.string(), z.number()).default({}),
  BaselineOpusUsd: z.number().default(0),
  PaidUsd: z.number().default(0),
  SavedUsd: z.number().default(0),
  SavedPct: z.number().default(0),
  AutorechargeEnabled: z.boolean().default(false),
  AutorechargeThresholdUsd: z.number().default(0),
  AutorechargeAmountUsd: z.number().default(0),
  Error: z.string().default(''),
});
export type CreditUsage = z.infer<typeof creditUsageSchema>;

export function getCreditUsage(organizationUUID: string): Promise<CreditUsage> {
  return postJson(
    'billing/getCreditUsage',
    { OrganizationUUID: organizationUUID },
    creditUsageSchema
  );
}

// ── routerTopupOneClick (saved-card, off-session) ────────────────────────────
export const topupOneClickSchema = z.object({
  BalanceUsd: z.number(),
  NeedsCheckout: z.boolean().default(false),
  Error: z.string().default(''),
});
export type TopupOneClick = z.infer<typeof topupOneClickSchema>;

export function routerTopupOneClick(
  organizationUUID: string,
  amountUsd: number,
  userUUID: string
): Promise<TopupOneClick> {
  return postJson(
    'billing/routerTopupOneClick',
    { OrganizationUUID: organizationUUID, AmountUsd: amountUsd, UserUUID: userUUID },
    topupOneClickSchema
  );
}

// ── routerTopupCheckout (hosted Stripe Checkout — first card / 3DS) ───────────
export const topupCheckoutSchema = z.object({
  CheckoutUrl: z.string().default(''),
  Error: z.string().default(''),
});
export type TopupCheckout = z.infer<typeof topupCheckoutSchema>;

export function routerTopupCheckout(
  organizationUUID: string,
  amountUsd: number,
  userUUID: string
): Promise<TopupCheckout> {
  return postJson(
    'billing/routerTopupCheckout',
    { OrganizationUUID: organizationUUID, AmountUsd: amountUsd, UserUUID: userUUID },
    topupCheckoutSchema
  );
}

// ── routerTopupVerify (credit-on-return from Checkout) ───────────────────────
export const topupVerifySchema = z.object({
  BalanceUsd: z.number(),
  Credited: z.boolean().default(false),
  Error: z.string().default(''),
});
export type TopupVerify = z.infer<typeof topupVerifySchema>;

export function routerTopupVerify(
  organizationUUID: string,
  checkoutSessionId: string
): Promise<TopupVerify> {
  return postJson(
    'billing/routerTopupVerify',
    { OrganizationUUID: organizationUUID, CheckoutSessionId: checkoutSessionId },
    topupVerifySchema
  );
}

// ── routerAutorecharge (toggle/configure) ────────────────────────────────────
export const autorechargeSchema = z.object({
  Enabled: z.boolean(),
  ThresholdUsd: z.number(),
  AmountUsd: z.number(),
  Error: z.string().default(''),
});
export type AutorechargeResult = z.infer<typeof autorechargeSchema>;

export function setRouterAutorecharge(
  organizationUUID: string,
  enabled: boolean,
  thresholdUsd = 0,
  amountUsd = 0
): Promise<AutorechargeResult> {
  return postJson(
    'billing/routerAutorecharge',
    {
      OrganizationUUID: organizationUUID,
      Enabled: enabled,
      ThresholdUsd: thresholdUsd,
      AmountUsd: amountUsd,
    },
    autorechargeSchema
  );
}

// ── routerInvoiceUrl (lazy Stripe receipt link) ──────────────────────────────
export const invoiceUrlSchema = z.object({
  Url: z.string().default(''),
  Error: z.string().default(''),
});
export type InvoiceUrl = z.infer<typeof invoiceUrlSchema>;

export function routerInvoiceUrl(
  organizationUUID: string,
  paymentRef: string
): Promise<InvoiceUrl> {
  return postJson(
    'billing/routerInvoiceUrl',
    { OrganizationUUID: organizationUUID, PaymentRef: paymentRef },
    invoiceUrlSchema
  );
}
